import React from "react";
import {
  IonCol,
  IonInput,
  IonLabel,
  IonLoading,
  IonRow,
  IonSelectOption,
  IonSkeletonText,
} from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import MaxWidthContainer from "../UI/MaxWidthContainer/MaxWidthContainer";
import PageTitle from "../PageTitle/PageTitle";
import CommonFormElement from "../UI/CommonFormElements/CommonFormElement";
import Select from "../Select/Select";
import { CustomPhoneInput } from "../CustomPhoneInput/CustomPhoneInput";
import ContinueButton from "../ContinueButton/ContinueButton";
import classNames from "classnames/bind";
import styles from "./ShippingAddressComponent.module.scss";
import commonFormStyles from "../UI/CommonFormElements/CommonFormElements.module.scss";
import { DatetimeChangeEventDetail } from "@ionic/core/components";
import { ShippingAddressManually } from "../ShippingAddressManually/ShippingAddressManually";
import { useCustomerAddressesStateData } from "../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { SearchAddressInput } from "../SearchAddressInput/SearchAddressInput";
import Checkbox from "../Checkbox/Checkbox";
import { PaymentTabButton, ShippingTabButton } from "../../types";
import { Switcher } from "../Switcher/Switcher";
import { SwitcherSkeleton } from "../UI/SwitcherSkeleton/SwitcherSkeleton";
import { useAuth } from "../../contexts/auth-context";
import { CustomerAddressInput } from "./CustomerAddressInput/CustomerAddressInput";
import { useConfig } from "../../providers/config";
import { useOrderData } from "../../providers/orderDataStore";
import { Address, ISODate, PickupPoint, ShippingMethodDeliveryType, ShippingMethodType } from "@core/types";
import { SelectPickupPoint } from "./SelectPickupPoint/SelectPickupPoint";
import { generateDeliveryRangeText } from "../../utils/utils";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import useGetDeliveryDatesQuery from "../../providers/shippingAndPayment/useGetDeliveryDatesQuery";
import { SelectDeliveryDate } from "./SelectDeliveryDate/SelectDeliveryDate";
import useGetDeliveryIntervalsQuery from "../../providers/shippingAndPayment/useGetDeliveryIntervalsQuery";
import { useCustomerAddressesStorageData } from "../../providers/customerAddressesStorage";
import { useMarketMode } from "../../providers/marketMode";
import Page from "../../pages/page-layout";
import useIsViewFocused from "../../hooks/useIsViewFocused";
import useGetCurrentAddress from "../../hooks/useGetCurrentAddress";
import { useTranslation } from "react-i18next";
import { createSafeDate, getSafeISODate, getSafeISOString } from "../../utils/dateUtils";

const cx = classNames.bind(styles);
const commonForm = classNames.bind(commonFormStyles);

interface IShippingAddressComponent {
  setShippingMethod: (methodId: string) => void;
  setPaymentMethod: (methodId: string) => void;
  shippingTabsButtons: ShippingTabButton[];
  paymentTabsButtons: PaymentTabButton[];
  showLoading: boolean;
  isPhoneNumberInputValid: boolean;
  setIsPhoneNumberInputValid: (value: boolean) => void;
  handleDeliveryDate: (e: CustomEvent<DatetimeChangeEventDetail>) => void;
  isWhatsappPhoneNumberValid: boolean;
  setIsWhatsappPhoneNumberValid: (value: boolean) => void;
  isFormValid: () => boolean;
  submit: () => void;
  pickupPoints: PickupPoint[] | undefined;
}

const ShippingAddressComponent: React.FC<IShippingAddressComponent> = ({
  setShippingMethod,
  setPaymentMethod,
  paymentTabsButtons,
  shippingTabsButtons,
  showLoading,
  isPhoneNumberInputValid,
  setIsPhoneNumberInputValid,
  handleDeliveryDate,
  isWhatsappPhoneNumberValid,
  setIsWhatsappPhoneNumberValid,
  isFormValid,
  submit,
  pickupPoints,
}) => {
  const isViewFocused = useIsViewFocused();
  const { t } = useTranslation();

  const { isUserAuthenticated } = useAuth();

  const config = useConfig();
  const {
    firstName,
    lastName,
    deliveryDate,
    deliveryInterval,
    shippingMethod,
    paymentMethod,
    phoneNumber,
    customerNote,
    whatsAppNumber,
    // address,
    updateOrderData,
  } = useOrderData();

  const address = useGetCurrentAddress();

  const { activeShippingZone } = useActiveOrMainShippingZone();

  const isoDeliveryDate = getSafeISODate(deliveryDate);

  const { data: deliveryDateList } = useGetDeliveryDatesQuery(
    { shippingMethodId: shippingMethod?.id },
    {
      enabled: config?.isDeliveryDateEnabled,
      onSuccess: (data) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if ((!deliveryDate || deliveryDate.getTime() < today.getTime()) && data && data.length > 0) {
          const safeDate = createSafeDate(data[0]);
          if (safeDate) {
            updateOrderData({ deliveryDate: safeDate });
          }
        }
      },
    },
  );
  const { data: deliveryIntervals = [], isLoading: isDeliveryIntervalsLoading } = useGetDeliveryIntervalsQuery(
    {
      shippingMethodId: shippingMethod?.id,
      date: isoDeliveryDate,
    },
    {
      enabled: Boolean(config?.isDeliveryIntervalEnabled && isoDeliveryDate),
      onSuccess: () => {
        updateOrderData({ deliveryInterval: "" });
      },
    },
  );

  const { setIsAddressModalOpen, isAddressModalOpen } = useCustomerAddressesStateData();
  const { setCurrentShippingAddressId, currentShippingAddressId } = useCustomerAddressesStorageData();
  const { mode } = useMarketMode();

  // FIXME: ЭТОТ КУСОК КОДА РАНЕЕ СБРАСЫВАЛ АДРЕС ДОСТАВКИ В ЗАКАЗЕ И СБРАСЫВАЛ ВЫБРАННЫЙ АДРЕС В ИНПУТЕ
  // FIXME: НУЖНО ОТКЛЮЧАТЬ ЭТУ ВОЗМОЖНОСТЬ ПО ФЛАГУ
  // useEffect(() => {
  //   if (!!address) {
  //     setCurrentShippingAddressId(address.id);
  //   }
  // }, [address?.id]);
  //
  // useEffect(() => {
  //   updateOrderData({ address: null });
  //   setCurrentShippingAddressId(undefined);
  // }, [shippingMethod]);

  const updateShippingAddressData = (customerAddress: Address) => {
    if (!customerAddress) {
      return;
    }

    if (
      customerAddress.id !== currentShippingAddressId &&
      activeShippingZone &&
      customerAddress.city?.includes(activeShippingZone?.name)
    ) {
      setCurrentShippingAddressId(customerAddress.id!, "ShippingAddressComponent");
      // updateOrderData({
      //   address: customerAddress,
      // });
    }

    //   updateOrderData({
    //     address: customerAddress,
    //   });
    // }
  };

  const invalidTextCustomerAddressInput =
    activeShippingZone && address && !(address.fullAddress || address.city)?.includes(activeShippingZone.name)
      ? `${t("Select an address in the zone")} "${activeShippingZone.name}" ${t("or change the delivery zone on the main page")}`
      : "";

  return (
    <>
      <Page.HeaderDesktop breadcrumbs={[{ title: t("Order Detail") }]} />
      <IonLoading isOpen={showLoading} message={t("Please wait...")} />
      <MaxWidthContainer classList={"global-content"} smContainer={true} mdContainer={true} lgContainer={true}>
        <PageTitle hideTitleBreakpoint={"ion-hide-md-down"} title={t("Order Detail")} />
        <div className={cx("shippingWrapper")}>
          <form>
            <IonRow className={"ion-justify-content-center"}>
              <IonCol size={"12"} sizeMd={"6"}>
                <CommonFormElement
                  label={
                    isUserAuthenticated ? t("Phone") : t("An SMS code will be sent to this number to confirm the order")
                  }
                  isValid={isPhoneNumberInputValid}
                  errorMessage={t("Please enter a valid phone number")}
                >
                  <CustomPhoneInput
                    className={commonForm("commonElement", { commonInvalid: !isPhoneNumberInputValid })}
                    value={phoneNumber || ""}
                    onCustomChange={(val, isValid) => {
                      updateOrderData({ phoneNumber: val });
                      setIsPhoneNumberInputValid(isValid);
                    }}
                  />
                </CommonFormElement>
                <CommonFormElement label={t("First Name")} isValid={!!firstName}>
                  <IonInput
                    type="text"
                    value={firstName}
                    required
                    name="shipping_firstname"
                    onIonChange={(e) => updateOrderData({ firstName: e.detail.value || "" })}
                    placeholder={t("First name input placeholder")}
                    className={commonForm("commonElement", { commonInvalid: !firstName })}
                  />
                </CommonFormElement>
                <CommonFormElement label={`${t("Last Name")} (${t("optional")})`}>
                  <IonInput
                    type="text"
                    name="shipping_lastname"
                    value={lastName}
                    onIonChange={(e) => updateOrderData({ lastName: e.detail.value || "" })}
                    placeholder={t("Last name input placeholder")}
                    className={commonForm("commonElement")}
                  />
                </CommonFormElement>

                {mode === "market" &&
                  (showLoading || !shippingTabsButtons.length ? (
                    <div className={cx("switcher__wrapper")}>
                      <SwitcherSkeleton />
                    </div>
                  ) : (
                    <div className={cx("switcher__wrapper")}>
                      <IonLabel className={cx("switcher__label")}>{t("Shipping Method")}</IonLabel>
                      <Switcher
                        buttons={shippingTabsButtons}
                        value={shippingMethod?.methodId}
                        setValue={setShippingMethod}
                      />
                      {shippingMethod?.methodDeliveryType === ShippingMethodDeliveryType.nextDay && (
                        <p className="m-1 text-slate-700 text-sm">
                          {t("Delivery date")}: <strong>{generateDeliveryRangeText(shippingMethod)}</strong>
                        </p>
                      )}
                      {!shippingMethod && (
                        <p className="m-1 text-[var(--ion-color-danger)] text-sm">
                          {t("Please, select the shipping method")}
                        </p>
                      )}
                    </div>
                  ))}
                {showLoading || !paymentTabsButtons.length ? (
                  <div className={cx("switcher__wrapper")}>
                    <SwitcherSkeleton />
                  </div>
                ) : (
                  <div className={cx("switcher__wrapper")}>
                    <IonLabel className={cx("switcher__label")}>{t("Payment Method")}</IonLabel>
                    <Switcher buttons={paymentTabsButtons} setValue={setPaymentMethod} value={paymentMethod?.id} />
                    {!paymentMethod && (
                      <p className="m-1 text-[var(--ion-color-danger)] text-sm">
                        {t("Please, select the payment method")}
                      </p>
                    )}
                  </div>
                )}

                {shippingMethod?.methodType === ShippingMethodType.pickupPoint ? (
                  <SelectPickupPoint 
                    pickupPoints={pickupPoints ?? []}
                    onSelectChange={(point) => {
                      if (point) {
                        const { id, name, address, latitude, longitude } = point;
                        // updateOrderData({
                        // address: {
                        //   id,
                        //   name,
                        //   fullAddress: address,
                        //   latitude,
                        //   longitude,
                        // },
                        // });
                      } else {
                        // updateOrderData({
                        // address: null,
                        // });
                      }
                    }}
                    initialPointId={address?.id}
                  />
                ) : config?.shipping.showOldAddressInputOnShippingPage ? (
                  config?.addressSettings.showCustomerAddresses ? (
                    // THERE HE IS
                    <CustomerAddressInput
                      selectDisabled={config?.isExpressDeliveryEnabled! && mode === "express"}
                      onSelectChange={updateShippingAddressData}
                      invalidText={invalidTextCustomerAddressInput}
                      setUnAuthCustomerAddress={(address) => {
                        setCurrentShippingAddressId(address.id, "ShippingAddressComponent");

                        // updateOrderData({
                        //   address: {
                        //     id: address.id,
                        //     fullAddress: address ? getCustomerFullAddress(address as Address) : "",
                        //     isFlat: !!address.isFlat,
                        //     entrance: address.entrance ?? "",
                        //     apartmentNumber: address.apartmentNumber ?? "",
                        //     latitude: address.latitude ?? "",
                        //     longitude: address.longitude ?? "",
                        //     city: address.city ?? "",
                        //     intercom: address.intercom ?? "",
                        //     state: address.state ?? "",
                        //     street: address.street ?? "",
                        //     house: address.house ?? "",
                        //     floor: address.floor ?? "",
                        //     name: "",
                        //   },
                        // });
                      }}
                    />
                  ) : (
                    <>
                      <SearchAddressInput
                        invalid={!address}
                        disabled={mode === "express"}
                        label={t("Delivery address")}
                        wrapperItemStyles={cx("shipping__addressManually")}
                        address={address?.fullAddress ?? ""}
                        onOptionSelect={({ address: sAddress }) => {
                          const fullAddress = sAddress?.locality + ", " + sAddress?.street + ", " + sAddress?.house;

                          // updateOrderData({
                          //   address: {
                          //     apartmentNumber: address?.apartmentNumber ?? "",
                          //     city: sAddress?.locality ?? "",
                          //     state: sAddress?.locality ?? "",
                          //     street: sAddress?.street ?? "",
                          //     house: sAddress?.house ?? "",
                          //     entrance: address?.entrance ?? "",
                          //     isFlat: address?.isFlat ?? false,
                          //     floor: address?.floor ?? "",
                          //     intercom: "",
                          //     fullAddress,
                          //     latitude: String(sAddress?.location ? sAddress?.location[0] : ""),
                          //     longitude: String(sAddress?.location ? sAddress?.location[1] : ""),
                          //   },
                          // });
                        }}
                      />

                      {config?.addressSettings.showFlatDelivery && (
                        <>
                          <Checkbox
                            checked={address?.isFlat ?? false}
                            itemClassName={cx("checkboxItem")}
                            label={t("Delivery to the apartment")}
                            onIonChange={(e: any) => {
                              // updateOrderData({
                              //   address: { ...address!, isFlat: e.detail.checked },
                              // });
                            }}
                          />
                          <div className={cx("dropdown", { dropdown__active: address?.isFlat ?? false })}>
                            {address?.isFlat && (
                              <>
                                <CommonFormElement label={t("Entrance")} isValid={!!address?.entrance}>
                                  <IonInput
                                    type="text"
                                    required={address?.isFlat ?? false}
                                    name="entrance"
                                    value={address?.entrance}
                                    onIonChange={(e) => {
                                      // updateOrderData({
                                      //   address: { ...address!, entrance: e.detail.value! },
                                      // });
                                    }}
                                    placeholder={t("Entrance input placeholder")}
                                    className={commonForm("commonElement", { commonInvalid: !address?.entrance })}
                                  />
                                </CommonFormElement>
                                <CommonFormElement label={t("Floor")} isValid={!!address?.floor}>
                                  <IonInput
                                    required={address?.isFlat ?? false}
                                    type="text"
                                    name="floor"
                                    value={address?.floor}
                                    onIonChange={(e) => {
                                      // updateOrderData({
                                      //   address: { ...address!, floor: e.detail.value! },
                                      // });
                                    }}
                                    placeholder={t("Floor input placeholder")}
                                    className={commonForm("commonElement", { commonInvalid: !address?.floor })}
                                  />
                                </CommonFormElement>
                                <CommonFormElement label={t("Apartment number")} isValid={!!address?.apartmentNumber}>
                                  <IonInput
                                    required={address?.isFlat ?? false}
                                    type="text"
                                    name="apartment-number"
                                    value={address?.apartmentNumber}
                                    onIonChange={(e) => {
                                      // updateOrderData({
                                      //   address: { ...address!, apartmentNumber: e.detail.value! },
                                      // });
                                    }}
                                    placeholder={t("Apartment input number placeholder")}
                                    className={commonForm("commonElement", {
                                      commonInvalid: !address?.apartmentNumber,
                                    })}
                                  />
                                </CommonFormElement>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )
                ) : !Capacitor.isNativePlatform() ? (
                  <CustomerAddressInput
                    selectDisabled={config?.isExpressDeliveryEnabled! && mode === "express"}
                    setUnAuthCustomerAddress={(address) => {
                      setCurrentShippingAddressId(address.id, "ShippingAddressComponent");
                      // updateOrderData({
                      //   address: {
                      //     id: address.id,
                      //     fullAddress: address ? getCustomerFullAddress(address as Address) : "",
                      //     isFlat: !!address.isFlat,
                      //     entrance: address.entrance ?? "",
                      //     apartmentNumber: address.apartmentNumber ?? "",
                      //     latitude: address.latitude ?? "",
                      //     longitude: address.longitude ?? "",
                      //     city: address.city ?? "",
                      //     intercom: address.intercom ?? "",
                      //     state: address.state ?? "",
                      //     street: address.street ?? "",
                      //     house: address.house ?? "",
                      //     name: "",
                      //     floor: address.floor ?? "",
                      //   },
                      // });
                    }}
                    onSelectChange={updateShippingAddressData}
                    invalidText={invalidTextCustomerAddressInput}
                  />
                ) : (
                  <>
                    <CommonFormElement
                      classListItem={cx("shipping__addressManually")}
                      label={t("Delivery address")}
                      isValid={!!currentShippingAddressId}
                    >
                      <SearchAddressInput
                        onOptionSelect={({ address: sAddress }) => {
                          const fullAddress = sAddress?.locality + ", " + sAddress?.street + ", " + sAddress?.house;

                          // updateOrderData({
                          //   address: {
                          //     apartmentNumber: address?.apartmentNumber ?? "",
                          //     city: sAddress?.locality ?? "",
                          //     state: sAddress?.locality ?? "",
                          //     street: sAddress?.street ?? "",
                          //     house: sAddress?.house ?? "",
                          //     entrance: address?.entrance ?? "",
                          //     isFlat: address?.isFlat ?? false,
                          //     floor: address?.floor ?? "",
                          //     intercom: "",
                          //     fullAddress,
                          //     latitude: String(sAddress?.location ? sAddress?.location[0] : ""),
                          //     longitude: String(sAddress?.location ? sAddress?.location[1] : ""),
                          //   },
                          // });
                        }}
                        address={address?.fullAddress ?? ""}
                        invalid={!address}
                        disabled={config?.isExpressDeliveryEnabled}
                      />
                    </CommonFormElement>

                    {config?.addressSettings.showFlatDelivery && (
                      <>
                        <Checkbox
                          checked={address?.isFlat ?? false}
                          itemClassName={cx("checkboxItem")}
                          label={t("Delivery to the apartment")}
                          onIonChange={(e: any) => {
                            // updateOrderData({
                            //   address: { ...address!, isFlat: e.detail.checked },
                            // });
                          }}
                        />
                        <div className={cx("dropdown", { dropdown__active: address?.isFlat ?? false })}>
                          {address?.isFlat && (
                            <>
                              <CommonFormElement label={t("Entrance")} isValid={!!address?.entrance}>
                                <IonInput
                                  type="text"
                                  required={address?.isFlat ?? false}
                                  name="entrance"
                                  value={address?.entrance}
                                  onIonChange={(e) => {
                                    // updateOrderData({
                                    //   address: { ...address!, entrance: e.detail.value! },
                                    // });
                                  }}
                                  placeholder={t("Entrance input placeholder")}
                                  className={commonForm("commonElement", { commonInvalid: !address?.entrance })}
                                />
                              </CommonFormElement>
                              <CommonFormElement label={t("Floor")} isValid={!!address?.floor}>
                                <IonInput
                                  required={address?.isFlat ?? false}
                                  type="text"
                                  name="floor"
                                  value={address?.floor}
                                  onIonChange={(e) => {
                                    // updateOrderData({
                                    //   address: { ...address!, floor: e.detail.value! },
                                    // });
                                  }}
                                  placeholder={t("Floor input placeholder")}
                                  className={commonForm("commonElement", { commonInvalid: !address?.floor })}
                                />
                              </CommonFormElement>
                              <CommonFormElement label={t("Apartment number")} isValid={!!address?.apartmentNumber}>
                                <IonInput
                                  required={address?.isFlat ?? false}
                                  type="text"
                                  name="apartment-number"
                                  value={address?.apartmentNumber}
                                  onIonChange={(e) => {
                                    // updateOrderData({
                                    //   address: { ...address!, apartmentNumber: e.detail.value! },
                                    // });
                                  }}
                                  placeholder={t("Apartment input number placeholder")}
                                  className={commonForm("commonElement", {
                                    commonInvalid: !address?.apartmentNumber,
                                  })}
                                />
                              </CommonFormElement>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                <ShippingAddressManually
                  onDismiss={() => {
                    setIsAddressModalOpen(false);
                  }}
                  isOpen={isAddressModalOpen && isViewFocused}
                />

                {mode === "market" &&
                  config?.isDeliveryIntervalEnabled &&
                  !!deliveryDateList &&
                  !!shippingMethod?.methodId &&
                  shippingMethod?.methodDeliveryType !== ShippingMethodDeliveryType.nextDay && (
                    <>
                      <SelectDeliveryDate
                        deliveryDateList={deliveryDateList}
                        isValid={!!deliveryDate}
                        onChange={handleDeliveryDate}
                        value={getSafeISOString(deliveryDate) ?? null}
                        className={commonForm("commonElement")}
                      />

                      <CommonFormElement label={t("Delivery interval")} isValid={!!(deliveryDate && deliveryInterval)}>
                        {isDeliveryIntervalsLoading ? (
                          <IonSkeletonText animated className="h-[51px] rounded-3xl" />
                        ) : (
                          <Select
                            value={deliveryInterval}
                            disabled={!deliveryInterval && deliveryIntervals.length < 1}
                            placeholder={t("Select delivery interval")}
                            onChange={(e: any) => updateOrderData({ deliveryInterval: e.detail.value })}
                            isValid={!!(deliveryDate && deliveryInterval)}
                          >
                            {deliveryIntervals.length > 0 &&
                              deliveryIntervals.map((interval) => {
                                return (
                                  <IonSelectOption key={interval.id} value={interval.text}>
                                    {interval.text}
                                  </IonSelectOption>
                                );
                              })}
                          </Select>
                        )}
                      </CommonFormElement>
                    </>
                  )}
                <CommonFormElement label={`${t("Order note")} (${t("optional")})`}>
                  <IonInput
                    type="text"
                    name="order-note"
                    value={customerNote}
                    onIonChange={(e) => updateOrderData({ customerNote: e.detail.value || "" })}
                    className={commonForm("ion-no-padding", "commonElement")}
                  />
                </CommonFormElement>
                <CommonFormElement
                  label={`${t("WhatsApp number")} (${t("optional")})`}
                  isValid={!whatsAppNumber || isWhatsappPhoneNumberValid}
                  errorMessage={t("Please enter a valid phone number")}
                >
                  <CustomPhoneInput
                    className={commonForm("commonElement", {
                      commonInvalid: whatsAppNumber && !isWhatsappPhoneNumberValid,
                    })}
                    value={whatsAppNumber}
                    onCustomChange={(val, isValid) => {
                      updateOrderData({ whatsAppNumber: val });
                      setIsWhatsappPhoneNumberValid(isValid);
                    }}
                  />
                </CommonFormElement>

                <div className={cx("ion-hide-md-down")}>
                  <ContinueButton
                    onClick={() => submit()}
                    disabled={
                      !isFormValid() ||
                      (address?.isFlat && (!address || !address.apartmentNumber || !address.floor || !address.entrance))
                    }
                    buttonText={t("Next")}
                  />
                </div>
              </IonCol>
            </IonRow>
          </form>
        </div>
      </MaxWidthContainer>
      <Page.ContentFooter />
    </>
  );
};

export { ShippingAddressComponent };
